import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Card,
  Col,
  Container,
  Row,
  CardBody,
  Label,
  Form,
  Input,
} from "reactstrap";

import { addLens, getvision, getVisionType } from "service/api";

const MATERIAL_CODES = {
  "CR-39(plastic)": "CR39",
  "Polycarbonate": "POLY",
  "Glass": "GLS",
  "Uncut": "UNC",
};

const COATING_CODES = {
  UC: "UC",
  HC: "HC",
  "HMC Blue": "HMC",
  "SHMC Blue": "SHMC",
};

const PHOTO_CODE = "PH";

const SUBTYPE_CODES = {
  Stock: "STK",
  Surface: "SRF",
};

const COATING_PRICE_KEYS = {
  UC: "uc",
  HC: "hc",
  "HMC Blue": "hmc_blue",
  "SHMC Blue": "shmc_blue",
};

const defaultValue = {
  lens_code: "",
  lens_material: "",
  lens_vision: "",
  lens_vision_name: "",
  lens_subvision: "",
  lens_subvision_name: "",
  sub_type: "",
  lens_type: "",
  photochromic: false,
  lens_sph: "",
  lens_cyl: "",
  lens_hc: "",
  diameter: "",
  price: "",
};

const sanitizeVisionCode = (visionName = "") => {
  const compact = visionName.replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
  return compact.slice(0, 3) || "GEN";
};

const generateLensCode = ({ material, subType, coating, visionName, photochromic }) => {
  const materialCode = MATERIAL_CODES[material] || "GEN";
  const visionCode = sanitizeVisionCode(visionName);
  const subTypeCode = SUBTYPE_CODES[subType] || "TYP";
  const coatingCode = COATING_CODES[coating] || "COAT";
  const sequence = Date.now().toString().slice(-4);
  const photoCode = photochromic ? `-${PHOTO_CODE}` : "";

  return `${materialCode}-${visionCode}-${subTypeCode}-${coatingCode}${photoCode}-${sequence}`;
};

const AddLens = () => {
  const [lens, setLens] = useState(defaultValue);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [vision, setVision] = useState({ users: [] });
  const [visionTypes, setVisionTypes] = useState([]);
  const navigate = useNavigate();

  const togglePhotochromic = () => {
    setLens((prev) => {
      const updated = {
        ...prev,
        photochromic: !prev.photochromic,
      };

      updated.lens_code = buildLensCode(updated);
      return updated;
    });

    setErrors((prev) => ({ ...prev, photochromic: "" }));
  };

  useEffect(() => {
    document.title = "Lens | Add Lens";
    getvision()
      .then((response) => setVision({ users: response.data || [] }))
      .catch((error) => console.error("Error fetching vision:", error));
    getVisionType()
      .then((response) => setVisionTypes(response.data || []))
      .catch((error) => console.error("Error fetching vision types:", error));
  }, []);

  const buildLensCode = (data) => {
    if (!data.lens_material || !data.sub_type || !data.lens_type) {
      return "";
    }

    return generateLensCode({
      material: data.lens_material,
      subType: data.sub_type,
      coating: data.lens_type,
      visionName: data.lens_vision_name,
      photochromic: data.photochromic,
    });
  };

  const onValueChange = (e) => {
    const { name, value, type, checked } = e.target;

    setLens((prev) => {
      const updated = { ...prev, [name]: type === "checkbox" ? checked : value };

      if (name === "lens_vision") {
        const selectedVision = vision.users.find((item) => item._id === value);
        updated.lens_vision_name = selectedVision?.vision || "";
      }

      if (["lens_material", "lens_vision", "sub_type", "lens_type", "photochromic"].includes(name)) {
        updated.lens_code = buildLensCode(updated);
      }

      return updated;
    });

    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  useEffect(() => {
    const priceKey = COATING_PRICE_KEYS[lens.lens_type];

    if (!lens.lens_vision || !lens.sub_type || !priceKey) {
      return;
    }

    const matchedVisionType = visionTypes.find((item) => {
      const visionId = item?.vision?._id || item?.vision;
      return String(visionId) === String(lens.lens_vision) && item?.sub_type === lens.sub_type;
    });

    if (!matchedVisionType) {
      return;
    }

    const basePrice = parseFloat(matchedVisionType?.[priceKey]) || 0;
    const photoPrice = lens.photochromic ? (parseFloat(matchedVisionType?.photo) || 0) : 0;
    const mappedPrice = (basePrice + photoPrice).toString();

    setLens((prev) => {
      if (prev.price === mappedPrice) {
        return prev;
      }

      return {
        ...prev,
        price: mappedPrice,
        lens_subvision: matchedVisionType?._id || "",
        lens_subvision_name: matchedVisionType?.sub_type || prev.sub_type,
      };
    });
  }, [lens.lens_vision, lens.sub_type, lens.lens_type, lens.photochromic, visionTypes]);

  const validate = () => {
    const formErrors = {};

    if (!lens.lens_code) formErrors.lens_code = "Lens code could not be generated";
    if (!lens.lens_material) formErrors.lens_material = "Lens category / material is required";
    if (!lens.lens_vision) formErrors.lens_vision = "Vision category is required";
    if (!lens.sub_type) formErrors.sub_type = "Sub-type is required";
    if (!lens.lens_type) formErrors.lens_type = "Lens coating is required";

    setErrors(formErrors);
    return Object.keys(formErrors).length === 0;
  };

  const handleAddLens = async (event) => {
    event.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      await addLens({ ...lens });
      toast.success("Lens added successfully", {
        position: "top-center",
        autoClose: 1500,
      });
      setLens(defaultValue);
      navigate("/lens");
    } catch (error) {
      toast.error("Failed to add lens");
      console.error("Add lens error:", error);
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = { fontSize: 13, borderRadius: 6, borderColor: "#e0e0e0" };
  const labelStyle = { fontSize: 12, fontWeight: 600, color: "#555", marginBottom: 4 };
  const cardSectionStyle = {
    border: "none",
    borderRadius: 10,
    boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
  };

  const sectionHeader = (num, title) => (
    <div
      style={{
        background: "linear-gradient(90deg,#f46a6a,#ff8282)",
        padding: "10px 20px",
        borderRadius: "10px 10px 0 0",
        display: "flex",
        alignItems: "center",
        gap: 8,
        marginBottom: 0,
      }}
    >
      <span
        style={{
          background: "rgba(255,255,255,0.25)",
          color: "#fff",
          borderRadius: "50%",
          width: 22,
          height: 22,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 11,
          fontWeight: 800,
          flexShrink: 0,
        }}
      >
        {num}
      </span>
      <span style={{ color: "#fff", fontWeight: 700, fontSize: 13 }}>{title}</span>
    </div>
  );

  return (
    <div className="page-content">
      <Container fluid>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h4 className="mb-0" style={{ fontWeight: 700, color: "#343a40" }}>
              Add Lens
            </h4>
            <p className="mb-0 text-muted" style={{ fontSize: 12 }}>
              Create a new lens record
            </p>
          </div>
          <Link
            to="/lens"
            className="btn btn-sm px-4 py-2"
            style={{ background: "#f46a6a", color: "#fff", fontWeight: 600, borderRadius: 6, border: "none" }}
          >
            Back to Lens List
          </Link>
        </div>

        <Form onSubmit={handleAddLens}>
          <Card className="mb-3" style={cardSectionStyle}>
            {sectionHeader(1, "Lens Setup")}
            <CardBody className="pt-3">
              <Row>
                <Col md={4}>
                  <div className="mb-3">
                    <Label style={labelStyle}>
                      Lens Code
                      <span style={{ fontSize: 10, color: "#999", fontWeight: 400, marginLeft: 6 }}>
                        (auto-generated)
                      </span>
                    </Label>
                    <div style={{ display: "flex", gap: 6 }}>
                      <Input
                        readOnly
                        value={lens.lens_code}
                        placeholder="Generated after selecting type fields"
                        style={{
                          ...inputStyle,
                          background: "#f8f8f8",
                          color: "#f46a6a",
                          fontWeight: 700,
                          letterSpacing: 0.8,
                          fontSize: 13,
                        }}
                      />
                      <button
                        type="button"
                        title="Regenerate code"
                        onClick={() => setLens((prev) => ({ ...prev, lens_code: buildLensCode(prev) }))}
                        style={{
                          padding: "0 12px",
                          border: "1px solid #f46a6a",
                          borderRadius: 6,
                          background: "#fff5f5",
                          color: "#f46a6a",
                          cursor: "pointer",
                          fontSize: 14,
                        }}
                      >
                        R
                      </button>
                    </div>
                    <div style={{ fontSize: 11, color: "#aaa", marginTop: 3 }}>
                      Format uses material, vision, sub-type, and coating.
                    </div>
                    {errors.lens_code && (
                      <div className="text-danger" style={{ fontSize: 11 }}>
                        {errors.lens_code}
                      </div>
                    )}
                  </div>
                </Col>

                <Col md={4}>
                  <div className="mb-3">
                    <Label style={labelStyle}>Lens Category / Material <span className="text-danger">*</span></Label>
                    <select
                      name="lens_material"
                      className="form-control"
                      style={inputStyle}
                      value={lens.lens_material}
                      onChange={onValueChange}
                    >
                      <option value="">Select material</option>
                      <option value="CR-39(plastic)">CR-39(plastic)</option>
                      <option value="Polycarbonate">Polycarbonate</option>
                      <option value="Glass">Glass</option>
                      <option value="Uncut">Uncut</option>
                    </select>
                    {errors.lens_material && (
                      <div className="text-danger" style={{ fontSize: 11 }}>
                        {errors.lens_material}
                      </div>
                    )}
                  </div>
                </Col>

                <Col md={4}>
                  <div className="mb-3">
                    <Label style={labelStyle}>Vision Category <span className="text-danger">*</span></Label>
                    <select
                      name="lens_vision"
                      className="form-control"
                      style={inputStyle}
                      value={lens.lens_vision}
                      onChange={onValueChange}
                    >
                      <option value="">Select vision category</option>
                      {vision.users.map((item) => (
                        <option key={item._id} value={item._id}>
                          {item.vision}
                        </option>
                      ))}
                    </select>
                    {errors.lens_vision && (
                      <div className="text-danger" style={{ fontSize: 11 }}>
                        {errors.lens_vision}
                      </div>
                    )}
                  </div>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <div className="mb-3">
                    <Label style={labelStyle}>Sub-type <span className="text-danger">*</span></Label>
                    <select
                      name="sub_type"
                      className="form-control"
                      style={inputStyle}
                      value={lens.sub_type}
                      onChange={onValueChange}
                    >
                      <option value="">Select sub-type</option>
                      <option value="Stock">Stock</option>
                      <option value="Surface">Surface</option>
                    </select>
                    {errors.sub_type && (
                      <div className="text-danger" style={{ fontSize: 11 }}>
                        {errors.sub_type}
                      </div>
                    )}
                  </div>
                </Col>

                <Col md={6}>
                  <div className="mb-3">
                    <Label style={labelStyle}>Lens Coating <span className="text-danger">*</span></Label>
                    <select
                      name="lens_type"
                      className="form-control"
                      style={inputStyle}
                      value={lens.lens_type}
                      onChange={onValueChange}
                    >
                      <option value="">Select coating</option>
                      <option value="UC">UC - Uncoated</option>
                      <option value="HC">HC - Hard Coated</option>
                      <option value="HMC Blue">HMC Blue - Multi-coat blue</option>
                      <option value="SHMC Blue">SHMC Blue - Super Multi-Coat Blue</option>
                    </select>
                    {errors.lens_type && (
                      <div className="text-danger" style={{ fontSize: 11 }}>
                        {errors.lens_type}
                      </div>
                    )}
                  </div>
                </Col>
              </Row>

              <Row>
                <Col md={4}>
                  <div className="mb-3">
                    <Label style={labelStyle}>Photochromic</Label>
                    <div className="d-flex align-items-center" style={{ gap: 8, minHeight: 38 }}>
                      <Input
                        type="checkbox"
                        name="photochromic"
                        defaultChecked={!!lens.photochromic}
                        onClick={togglePhotochromic}
                        onChange={() => { }}
                        style={{ width: 16, height: 16, cursor: "pointer" }}
                      />
                      <span style={{ fontSize: 13, color: "#555" }}>Add photochromic price and code</span>
                    </div>
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>

          <Card className="mb-3" style={cardSectionStyle}>
            {sectionHeader(2, "Lens Details")}
            <CardBody className="pt-3">
              <Row>
                <Col md={3}>
                  <div className="mb-3">
                    <Label style={labelStyle}>SPH</Label>
                    <Input
                      type="text"
                      name="lens_sph"
                      placeholder="Enter SPH"
                      style={inputStyle}
                      value={lens.lens_sph}
                      onChange={onValueChange}
                    />
                  </div>
                </Col>

                <Col md={3}>
                  <div className="mb-3">
                    <Label style={labelStyle}>CYL</Label>
                    <Input
                      type="text"
                      name="lens_cyl"
                      placeholder="Enter CYL"
                      style={inputStyle}
                      value={lens.lens_cyl}
                      onChange={onValueChange}
                    />
                  </div>
                </Col>

                <Col md={3}>
                  <div className="mb-3">
                    <Label style={labelStyle}>HC</Label>
                    <Input
                      type="text"
                      name="lens_hc"
                      placeholder="Enter HC"
                      style={inputStyle}
                      value={lens.lens_hc}
                      onChange={onValueChange}
                    />
                  </div>
                </Col>

                <Col md={3}>
                  <div className="mb-3">
                    <Label style={labelStyle}>Diameter</Label>
                    <Input
                      type="text"
                      name="diameter"
                      placeholder="Enter diameter"
                      style={inputStyle}
                      value={lens.diameter}
                      onChange={onValueChange}
                    />
                  </div>
                </Col>
              </Row>

              <Row>
                <Col md={4}>
                  <div className="mb-3">
                    <Label style={labelStyle}>Price</Label>
                    <Input
                      type="text"
                      name="price"
                      placeholder="Enter price"
                      style={inputStyle}
                      value={lens.price}
                      onChange={onValueChange}
                    />
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>

          <div className="d-flex justify-content-end mb-4">
            <button
              type="submit"
              disabled={loading}
              style={{
                background: "linear-gradient(90deg,#f46a6a,#ff8282)",
                color: "#fff",
                fontWeight: 700,
                fontSize: 14,
                borderRadius: 8,
                boxShadow: "0 3px 12px rgba(244,106,106,0.4)",
                border: "none",
                padding: "10px 40px",
                cursor: loading ? "not-allowed" : "pointer",
                opacity: loading ? 0.7 : 1,
              }}
            >
              {loading ? "Saving..." : "Save Lens"}
            </button>
          </div>
        </Form>
      </Container>
    </div>
  );
};

export default AddLens;
